/**
 * Quando o access token esta' perto demais de vencer para valer a pena enviar.
 *
 * ## Incidente 2026-09-21 (item 7 / C11)
 *
 * `GET /notifications/unread-count` e `GET /tokens/balance` respondiam **401
 * intermitente** logo apos navegar, com sessao valida. O interceptor se
 * recupera (renova e repete), entao a tela funcionava — mas o 401 vermelho
 * ficava no console, e quando varias consultas saem juntas, TODAS levam 401
 * antes de a primeira renovar.
 *
 * A causa e' banal: o access token vale **15 minutos** e so era renovado
 * DEPOIS de um request falhar. Qualquer aba aberta mais que isso paga um 401
 * por lote de requisicoes.
 *
 * Saber o vencimento e' o suficiente para renovar ANTES. O token e' um JWT: o
 * `exp` esta' no payload, em claro (nao ha' verificacao de assinatura aqui —
 * quem valida e' o servidor; aqui so' se le uma data para decidir a hora de
 * renovar).
 */

/**
 * Margem antes do vencimento.
 *
 * Cobre o tempo de rede da requisicao e uma diferenca de relogio pequena entre
 * navegador e servidor — sem isso, um token "valido por 2 segundos" sairia e
 * chegaria vencido.
 */
export const TOKEN_EXPIRY_SKEW_SECONDS = 60;

/** `exp` do JWT, em milissegundos, ou `null` se nao der para ler. */
export function tokenExpiryMs(token: string | null | undefined): number | null {
  if (!token) return null;

  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    const normalizado = payload.replaceAll('-', '+').replaceAll('_', '/');
    const json = JSON.parse(globalThis.atob(normalizado)) as { exp?: unknown };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    // Token que nao e' JWT legivel: quem decide e' o servidor, como sempre.
    return null;
  }
}

/**
 * `true` quando vale a pena renovar ANTES de enviar a requisicao.
 *
 * Token ilegivel devolve `false` de proposito: sem saber o vencimento, o
 * caminho correto continua sendo enviar e deixar o 401 (e o retry) decidirem —
 * renovar por precaucao a cada request viraria um refresh em loop.
 */
export function shouldRefreshBeforeRequest(
  token: string | null | undefined,
  agoraMs: number = Date.now(),
): boolean {
  const expiraEm = tokenExpiryMs(token);
  if (expiraEm === null) return false;

  return expiraEm - agoraMs <= TOKEN_EXPIRY_SKEW_SECONDS * 1000;
}
